import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import {
  FileValidationParams,
  validateFileHeaders,
} from "../utils/validateFileHeaders";


export interface IOperator {
  id: number;
  name: string;
  cc: string;
  mcc: string[];
  mnc: string[];
  isDeleted: boolean;
  createdBy: string;
  createdDate: string;
  countryId: number;
  updatedBy?: string;
}
export interface OperatorHistory {
  id: number;
  snapshot: IOperator & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export interface OperatorHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: IOperator & { timestamp: string };
  diff?: Record<string, string>;
}

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export const exportOperator = async (
  params: Params<IOperator> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/operators/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "operators.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching operators:",
      error.response?.data?.message || error.message || error
    );
  }
};

export const fetchOperators = async (
  params: Params<IOperator>
): Promise<IPageable<IOperator>> => {
  try {
    console.log("Fetching operators...");
    const response = await axiosInstance.get("/operators", { params });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching operators:", error.message || error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the operators."
    );
  }
};

export const fetchOperator = async (
  id: IOperator["id"]
): Promise<IOperator> => {
  try {
    console.log(`Fetching operators/${id}...`);
    const response = await axiosInstance.get(`/operators/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching operators/${id}:`, error.message || error);
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching the operators/${id}.`
    );
  }
};

export const createOperator = async (
  request: IOperator
): Promise<IOperator> => {
  try {
    const response = await axiosInstance.post("/operators", request);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the operator."
    );
  }
};

export const editOperatorApi = async (id: IOperator["id"], data: IOperator) => {
  try {
    const response = await axiosInstance.put(`/operators/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the operator."
    );
  }
};

export const getOperatorById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/operators/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the operator details."
    );
  }
};

export const deleteOperatorApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete(`/operators`, { data: ids });
    console.log(`Operator with ID ${ids} deleted successfully.`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the operator."
    );
  }
};

export interface OperatorUploadParams {
  mcc: string;
  mnc: string;
  country: string;
  cc: string;
  operator: string;
}

export const uploadOperatorExcel = async (
  file: File,
  override: boolean,
  uploadParams: OperatorUploadParams
): Promise<void> => {
  const extension = file.name.split(".").pop()?.toLowerCase() || "xlsx";

  const validationParams: FileValidationParams = {
    alwaysRequired: [
      uploadParams.mcc,
      uploadParams.mnc,
      uploadParams.country,
      uploadParams.operator,
      uploadParams.cc,
    ],
    optionalFields: [],
  };

  await validateFileHeaders(file, extension, validationParams);

  const formData = new FormData();
  formData.append("file", file);

  const endpoint = extension === "csv" ? "csv" : "excel";

  const params = {
    override: override,
    countryHeader: uploadParams.country,
    mccHeader: uploadParams.mcc,
    mncHeader: uploadParams.mnc,
    ccHeader: uploadParams.cc,
    operatorHeader: uploadParams.operator,
  };

  try {
    const response = await axiosInstance.post(
      `/operators/${endpoint}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params,
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while uploading the operator Excel file."
    );
  }
};

export const useIOperatorQuery = (
  params: Params<IOperator>
): UseQueryResult<IPageable<IOperator>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };

  const queryKey = ["operators", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchOperators(effectiveParams),
  });
};
export const useOperatorQueryById = (
  operatorId: number
): UseQueryResult<IOperator, Error> => {
  return useQuery({
    queryKey: ["operator", operatorId],
    queryFn: async () => {
      return await fetchOperator(operatorId);
    },
    enabled: !!operatorId,
  });
};

export const getOperatorNameOptions = (operatorId: number) => ({
  queryKey: ["operator", operatorId],
  queryFn: async () => {
    try {
      return await fetchOperator(operatorId);
    } catch {
      return null;
    }
  },
  enabled: !!operatorId,
  select: (o: IOperator | null) => o?.name || "-",
});

export const getOperatorCcOptions = (operatorId: number) => ({
  queryKey: ["operator", operatorId],
  queryFn: async () => {
    return await fetchOperator(operatorId);
  },
  enabled: !!operatorId,
  select: (o: IOperator) => o.cc,
});

export const getOperatorMccOptions = (operatorId: number) => ({
  queryKey: ["operator", operatorId],
  queryFn: async () => {
    return await fetchOperator(operatorId);
  },
  enabled: !!operatorId,
  select: (o: IOperator) => o.mcc.join(","),
});

export const getOperatorMncOptions = (operatorId: number) => ({
  queryKey: ["operator", operatorId],
  queryFn: async () => {
    return await fetchOperator(operatorId);
  },
  enabled: !!operatorId,
  select: (o: IOperator) => o.mnc.join(","),
});

export async function fetchOperatorHistory(
  id: IOperator["id"],
  params: Params<OperatorHistory> = {}
): Promise<IPageable<IOperator & OperatorHistory>> {
  try {
    const response = await axiosInstance.get(`/operators/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params }
    });
    const data = response.data;
   return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Operator history for ID ${id}.`
    );
  }
}

/**
 * Get operator id by operator name (case-insensitive).
 * Fetches all operators and returns the id of the first match.
 */
export const getOperatorIdByName = async (operatorName: string): Promise<number | null> => {
  if (!operatorName) return null;
  try {
    const operatorsPage = await fetchOperators({});
    const found = operatorsPage.content.find(
      (op) => op.name === operatorName
    );
    return found ? found.id : null;
  } catch (err) {
    console.error("Error fetching operator by name:", err);
    return null;
  }
};

